import React from 'react'
import { Alert, Box, Button, Paper, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import Header from 'components/Header'
import ErrorList from 'components/ErrorList'
import CustomEmail from 'components/CustomEmail'
import useDocumentVerify from './hooks/useDocumentVerify'
import DocumentDisplay from './components/DocumentDisplay'
import VerifyForm from './components/VerifyForm'
import PageSelector from './components/PageSelector'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'

const AdminNurseResumeVerify: React.FC = () => {
    const { 
        docType, 
        files, 
        nurse, 
        form, 
        updateForm, 
        error,
        loading, 
        deleting, 
        save, 
        deleteFile, 
        scrollToFile, 
        futureBookings 
    } = useDocumentVerify()
    
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()

    const sendWorkReferenceRequest = React.useCallback(async () => {
        try {
            await api.sendWorkReferenceRequest(nurse.nurse_id)
            showAlert('success', 'Work reference request sent')
        } catch (error) {
            showAlert('error', 'Failed to send work reference request')
        }
    }, [api, nurse.nurse_id, showAlert])

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gridTemplateAreas: `'image form'`,
            gap: 2
        }}>
            <Box sx={{ gridArea: 'image', alignSelf: 'center' }}>
                <DocumentDisplay files={files} />
            </Box>
            <Box sx={{ gridArea: 'form', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Header variant='h3' text='Hygienist Resume'></Header>
                <VerifyForm form={form} updateForm={updateForm} error={error} docType={docType} />
                {error?.schema ? (
                    <Alert severity='error'><ErrorList errors={error.schema} /></Alert>
                ) : null}
                <Button variant="contained" color="primary" onClick={save} disabled={loading}>
                    {loading ? 'Please Wait ...' : 'Update Document Status'}
                </Button>
                {!form.approved && !isEmpty(futureBookings) ? (
                    <Alert variant='filled' severity='warning'>
                        This nurse has jobs booked in the future. <br />
                        If you disapprove this nurse, remember to cancel those bookings and find replacements for those jobs.
                    </Alert>
                ) : null}
                <PageSelector files={files} scrollToFile={scrollToFile} deleteFile={deleteFile} deleting={deleting} />
                <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Header variant='h2' text='Work Reference' />
                    <Typography variant='body1'>
                        Name: {nurse?.reference_from_name || 'Not Provided'} <br />
                        Email: {nurse?.reference_from_email || 'Not Provided'} <br />
                        Requested at: {nurse?.reference_requested_at?.toFormat('dd/MM/yyyy hh:mm a') || 'Not Requested Yet'} <br />
                        Received at: {nurse?.reference_received_at?.toFormat('dd/MM/yyyy hh:mm a') || 'Not Received Yet'} <br />
                    </Typography>
                    <Button variant='contained' color='primary' onClick={sendWorkReferenceRequest}>
                        { nurse?.reference_requested_at ? 'Resend' : 'Send' } Reference Request Email
                    </Button>
                </Paper>
                <CustomEmail
                    nurse={nurse}
                    subject='Action required before we can approve your account'
                />
            </Box>
        </Box>
    )
}

export default AdminNurseResumeVerify